import { useNavigationQuery } from "../navigation/NavigationQueryProvider";
import { stringSetQuery } from "../navigation/queryCodecs";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  Stack,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback } from "react";
import {
  EventLabellingModelRead,
  InferenceEnvironment,
} from "store/api/generatedApi";
import {
  findWinningModelIds,
  groupModelsByEnvironment,
  INFERENCE_ENVIRONMENT_CONFIG,
  INFERENCE_ENVIRONMENT_OPTIONS,
  STATUS_CONFIG,
} from "./modelDashboardHelpers";

type ModelListProps = Readonly<{
  models: readonly EventLabellingModelRead[];
  selectedModelId: number | null;
  onSelect: (modelId: number) => void;
}>;

export const ModelList = ({
  models,
  selectedModelId,
  onSelect,
}: ModelListProps) => {
  const grouped = groupModelsByEnvironment(models);
  const [collapsedEnvs, setCollapsedEnvs] = useNavigationQuery(
    "collapsedModelEnvironments",
    new Set<string>(),
    stringSetQuery,
    "replace"
  );

  const toggleEnv = useCallback(
    (env: InferenceEnvironment) => {
      setCollapsedEnvs((prev) => {
        const next = new Set(prev);
        if (next.has(env)) {
          next.delete(env);
        } else {
          next.add(env);
        }
        return next;
      });
    },
    [setCollapsedEnvs]
  );

  return (
    <List sx={{ py: 0 }} disablePadding>
      {INFERENCE_ENVIRONMENT_OPTIONS.map((env) => (
        <EnvironmentSection
          key={env}
          env={env}
          models={grouped[env]}
          winningModelIds={findWinningModelIds(grouped[env])}
          collapsed={collapsedEnvs.has(env)}
          onToggle={toggleEnv}
          selectedModelId={selectedModelId}
          onSelect={onSelect}
        />
      ))}
    </List>
  );
};

const EnvironmentSection = ({
  env,
  models,
  winningModelIds,
  collapsed,
  onToggle,
  selectedModelId,
  onSelect,
}: {
  env: InferenceEnvironment;
  models: readonly EventLabellingModelRead[];
  winningModelIds: Set<number>;
  collapsed: boolean;
  onToggle: (env: InferenceEnvironment) => void;
  selectedModelId: number | null;
  onSelect: (modelId: number) => void;
}) => {
  const { t } = useTenantTranslation();
  return (
    <>
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          bgcolor: "background.default",
          borderTop: 1,
          borderBottom: 1,
          borderColor: "divider",
          cursor: "pointer",
        }}
        onClick={() => {
          onToggle(env);
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" sx={{ p: 0.25 }} aria-label={t(env)}>
            {collapsed ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ExpandLessIcon fontSize="small" />
            )}
          </IconButton>
          <Chip
            size="small"
            label={t(env)}
            color={INFERENCE_ENVIRONMENT_CONFIG[env].chipColor}
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            {models.length}
          </Typography>
        </Stack>
      </Box>
      <Collapse in={!collapsed} unmountOnExit>
        {models.length === 0 ? (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t("noModelsInEnvironment")}
            </Typography>
          </Box>
        ) : (
          models.map((model) => (
            <ModelListItem
              key={model.id}
              model={model}
              isWinner={winningModelIds.has(model.id)}
              selected={model.id === selectedModelId}
              onSelect={onSelect}
            />
          ))
        )}
      </Collapse>
    </>
  );
};

const ModelListItem = ({
  model,
  isWinner,
  selected,
  onSelect,
}: {
  model: EventLabellingModelRead;
  isWinner: boolean;
  selected: boolean;
  onSelect: (modelId: number) => void;
}) => {
  const { t } = useTenantTranslation();
  return (
    <ListItemButton
      selected={selected}
      onClick={() => {
        onSelect(model.id);
      }}
      sx={{
        borderLeft: 3,
        borderColor: selected ? "primary.main" : "transparent",
        py: 1.25,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ width: "100%" }}
      >
        <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "fontWeightMedium" }}
            noWrap
          >
            {model.labelling_name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
            noWrap
          >
            {t("idPrefix")} {model.mlflow_run_id}
          </Typography>
        </Stack>
        <AuthorityBadge authority={model.authority} isWinner={isWinner} />
        <Chip
          size="small"
          label={t(model.status)}
          color={STATUS_CONFIG[model.status].tone}
          variant={STATUS_CONFIG[model.status].variant}
          sx={{ width: 76 }}
        />
      </Stack>
    </ListItemButton>
  );
};

const AuthorityBadge = ({
  authority,
  isWinner,
}: {
  authority: number | null | undefined;
  isWinner: boolean;
}) => {
  const { t } = useTenantTranslation();
  if (authority == null) {
    return (
      <Chip
        size="small"
        label={t("noAuthority")}
        variant="outlined"
        sx={{
          borderStyle: "dashed",
          borderColor: "text.disabled",
          color: "text.disabled",
          fontStyle: "italic",
          width: 110,
        }}
      />
    );
  }
  return (
    <Chip
      size="small"
      label={String(authority)}
      color="primary"
      variant={isWinner ? "filled" : "outlined"}
      sx={{
        width: 110,
        fontVariantNumeric: "tabular-nums",
        fontWeight: isWinner ? "fontWeightSemiBold" : "fontWeightRegular",
      }}
    />
  );
};
